import { Value } from "@wojtekmaj/react-daterange-picker/dist/cjs/shared/types";
import { addLogoToPdf } from "helpers";
import { t } from "i18next";
import { jsPDF as JsPDF } from "jspdf";
import { jsPDFDocument } from "jspdf-autotable";
import moment from "moment";

import { ADMIN_DELIVERIES_LABELS } from "../../constants";
import {
  DeliveriesLabels,
  DriverDeliveriesReportItem
} from "../../financial.types";
import { getDriversDeliveriesTotals } from "../getDriversDeliveriesTotals";
import { registerReportFont } from "../reportFont";
import { loadDriverAvatars } from "./loadDriverAvatars";
import { renderDeliveriesTable } from "../downloadOneDriverDeliveriesReport/renderDeliveriesTable";
import { getFonts } from "./getFonts";
import { renderDriverSummary } from "./renderDriverSummary";
import { renderTitleAndDateRange } from "./renderTitleAndDateRange";

/** Keep a driver's header block and the first rows of its table on one page. */
const MIN_SPACE_FOR_NEW_DRIVER = 190;

/** Air between two drivers' cards. */
const GAP_BETWEEN_DRIVERS = 22;

export const downloadAllDriversDeliveriesReport = async (
  items: DriverDeliveriesReportItem[],
  dateRange: Value,
  labels: DeliveriesLabels = ADMIN_DELIVERIES_LABELS
) => {
  const avatars = await loadDriverAvatars(items);

  const reportDateRange = Array.isArray(dateRange)
    ? `${moment(dateRange[0]).format("DD_MM_YYYY")} - ${moment(dateRange[1]).format("DD_MM_YYYY")}`
    : t("All time");

  const pdf = new JsPDF({
    unit: "px",
    orientation: "landscape"
  });

  addLogoToPdf(pdf);

  const font = await registerReportFont(pdf);

  const { normalFontSize, normalFontStyles, bigFontSize } = getFonts(font);

  pdf.setLineHeightFactor(1.3);

  renderTitleAndDateRange({
    pdf,
    normalFontSize,
    bigFontSize,
    dateRange,
    font
  });

  const pageHeight = pdf.internal.pageSize.getHeight();

  const nextStartY = (isFirstBlock: boolean) => {
    const finalY = (pdf as jsPDFDocument).lastAutoTable?.finalY ?? 80;
    const startY = isFirstBlock ? finalY + 10 : finalY + GAP_BETWEEN_DRIVERS;

    if (startY > pageHeight - MIN_SPACE_FOR_NEW_DRIVER) {
      pdf.addPage();

      return 40;
    }

    return startY;
  };

  items.forEach(({ driver, deliveries }, index) => {
    renderDriverSummary({
      pdf,
      title: `${driver.firstname} ${driver.lastname}`,
      totals: driver,
      labels,
      font,
      avatar: avatars.get(driver.id),
      startY: nextStartY(index === 0)
    });

    if (deliveries.length > 0) {
      renderDeliveriesTable({
        pdf,
        normalFontStyles,
        deliveries,
        labels,
        font,
        continuationTitle: `${driver.firstname} ${driver.lastname}`
      });
    }
  });

  if (items.length > 0) {
    renderDriverSummary({
      pdf,
      title: t("TOTAL"),
      totals: getDriversDeliveriesTotals(items.map((item) => item.driver)),
      labels,
      font,
      startY: nextStartY(false)
    });
  }

  const totalPages = pdf.getNumberOfPages();

  if (totalPages > 1) {
    for (let i = 1; i <= totalPages; i++) {
      pdf.setPage(i);

      const pageWidth = pdf.internal.pageSize.getWidth();

      pdf.setFontSize(10);

      pdf.text(
        `${t("Page")} ${i} ${t("of")} ${totalPages}`,
        pageWidth / 2,
        pageHeight - 10,
        {
          align: "center"
        }
      );
    }
  }

  pdf.save(`DriversDeliveriesReport-${reportDateRange}.pdf`);
};
